import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import {
  catchError,
  Observable,
  throwError,
  switchMap,
  BehaviorSubject,
  filter,
  take,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import { KeycloakService } from 'keycloak-angular';

@Injectable()
export class AddHeaderInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshDone$ = new BehaviorSubject<boolean>(false);

  constructor(private keycloak: KeycloakService) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const clonedRequest = req.clone({
      headers: req.headers.append('portal', environment.authClientId),
    });

    return next.handle(clonedRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          if (req.headers.has('X-Retry-After-Refresh')) {
            console.warn('401 on retried request — forcing logout');
            this.forceLogout();
            return throwError(() => error);
          }
          return this.handle401(clonedRequest, next);
        }
        return throwError(() => error);
      })
    );
  }

  private handle401(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {

    // FIX: Check whether the access token is already past its expiry time.
    // If it is, refreshing it would silently re-authenticate the user even
    // after TokenExpiryService has triggered logout — defeating the logout.
    // Only attempt a refresh when the token is still within its validity window
    // (i.e. the 401 was caused by a server-side rejection, not expiry).
    if (this.isTokenExpired()) {
      console.warn('Access token is expired — skipping refresh, forcing logout');
      this.forceLogout();
      return throwError(() => new HttpErrorResponse({ status: 401 }));
    }

    if (this.isRefreshing) {
      return this.refreshDone$.pipe(
        filter(done => done),
        take(1),
        switchMap(() => {
          const retryReq = request.clone({
            headers: request.headers.append('X-Retry-After-Refresh', 'true'),
          });
          return next.handle(retryReq);
        })
      );
    }

    this.isRefreshing = true;
    this.refreshDone$.next(false);

    return new Observable<HttpEvent<any>>(subscriber => {
      this.keycloak.updateToken(-1)
        .then(refreshed => {
          this.isRefreshing = false;
          this.refreshDone$.next(true);

          if (refreshed) {
            console.log('Token refreshed — retrying request');
          }

          const retryReq = request.clone({
            headers: request.headers.append('X-Retry-After-Refresh', 'true'),
          });
          next.handle(retryReq).subscribe(subscriber);
        })
        .catch((err) => {
          this.isRefreshing = false;
          this.refreshDone$.next(true);
          console.error('Token refresh failed:', err);
          this.forceLogout();
          subscriber.error(new HttpErrorResponse({ status: 401 }));
        });
    });
  }

  // Returns true when the access token's exp claim is in the past
  private isTokenExpired(): boolean {
    try {
      const exp = this.keycloak.getKeycloakInstance()?.tokenParsed?.exp;
      if (!exp) return true; // no token at all — treat as expired
      return Math.floor(Date.now() / 1000) >= exp;
    } catch {
      return true;
    }
  }

  private forceLogout(): void {
    this.clearLocalState();
    try {
      this.keycloak.logout(window.location.origin).catch(() => {
        console.warn('Keycloak logout failed — redirecting to login');
        window.location.href = window.location.origin;
      });
    } catch (e) {
      console.warn('Keycloak logout threw — redirecting to login');
      window.location.href = window.location.origin;
    }
  }

  private clearLocalState(): void {
    localStorage.removeItem('currentOrg');
    localStorage.removeItem('currentSender');
  }
}